/* eslint-disable @typescript-eslint/no-explicit-any */
import { pascalToKebabCase } from '../utils/string-case'
import { AttributeParser } from '../utils/attribute-parser'
import { TypeString } from '../models/type-string'

/**
 * Creates an HTML attribute that reflects to the decorated property. The HTML attribute is converted to kebab-case.
 *
 * Can optionally observe changes to the HTML attribute that will call the provided method name. The method that's called will be provided arguments
 * `oldVal` and `newVal` which will have the types provided in the `opts` parameter. This is useful for checking validity of new values. This method
 * will fire upon class initialization. Your class **must** extend `GbdsElement` class for this functionality to work. If you simply want to
 * reflect an attribute to a property then you **do not need to extend `GbdsElement`.
 *
 * @param type Optional argument that tells the runtime to parse the attribute as the correct type. If omitted defaults to `'string'`
 * @param changedFn Optional name of the function that is ran when the attribute is changed.
 *
 * **Note:** `array` and `object` property types set as HTML attributes *must* be stringified, e.g. `<some-component some-prop='{ "someMember": "abc", "anotherMember": "123" }'>`
 *
 * **Note**: This will *not* work, `document.querySelector('some-component').setAttribute('some-prop', { someMember: 'abc', anotherMember: 123 })`
 *
 * **Note**: This *will* work, `document.querySelector('some-component').someProp = { someMember: 'abc', anotherMember: 123 }`
 *
 * **Note:** In the examples below we've replaced the "at symbol" with the ＠ symbol (it looks similar but is not the same) because JSDoc escaping doesn't appear to be working.
 * In your code you will have to replace ＠ with the correct symbol, typically `shift + 2` on a US keyboard.
 * @example
 * ＠component('gb-some-component')
 * export class SomeComponent extends GbdsElement {
 * 		＠prop('number', 'doSomething')
 * 		count: number
 *
 * 		doSomething(oldValue: number, newValue: number) {
 *  		// Do something with the old and new value.
 * 		}
 * 		constructor() {
 * 			super()
 * 		}
 * }
 *
 * With the HTML:
 * <gb-some-component count="15"></gb-some-component>
 *
 * @yields
 * `this.count = 15`
 *
 * @example
 * ＠component('gb-some-component')
 * export class SomeComponent extends GbdsElement {
 * 		＠prop('object', 'doSomething')
 * 		someProp: object
 *
 * 		doSomething(oldValue: object, newValue: object) {
 *  		// Do something with the old and new value.
 * 		}
 * 		constructor() {
 * 			super()
 * 		}
 * }
 *
 * With the HTML:
 * <gb-some-component some-prop='{ "someMember": "abc", "anotherMember": "123" }'>
 *
 * @yields
 * `this.count = { someMember: 'abc', anotherMember: 123 }`
 */
export function prop(type: TypeString = 'string', changedFn: string = null) {
	return (target: any, key: string) => {
		const attributeName = pascalToKebabCase(key)

		if (changedFn) {
			//added due to linter upgrade
			// eslint-disable-next-line no-prototype-builtins
			if (target.hasOwnProperty(changedFn)) {
				target.constructor.observedProps.set(attributeName, { type, changedFn })
			} else {
				throw new Error(`Member \`${changedFn}\` does not exist on on target class.`)
			}
		}

		const getter = function () {
			return AttributeParser.fromAttribute(this.getAttribute(attributeName), type)
		}

		const setter = function (newVal: string) {
			this.setAttribute(attributeName, AttributeParser.toAttribute(newVal, type))
		}

		if (delete target[key]) {
			Object.defineProperty(target, key, {
				get: getter,
				set: setter,
				enumerable: true,
			})
		}
	}
}
